import { K8sProviderIcon } from '@cast/features-cluster-ui';
import { useContext, useMemo, useState, type ReactNode } from 'react';

import {
  Button,
  DialogFooter,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { ReportingTrialConnectionAlert, useTrial } from '@cast/shared-trial-ui';
import { K8sProvider } from '@cast/shared-types';
import { ClusterOnboardingPathEnum } from '@cast/types';
import { k8sProviderToCloud } from '@cast/utils';

import { InstallScript as KentInstallScript } from 'features/kent/components/InstallScript';
import { isSupportedCloudProvider } from 'features-op/permissions-center/utils';
import { useConnectAndEnableCastAiScriptQuery } from 'hooks/queries/cluster/useConnectAndEnableCastAiScriptQuery';
import { useReportingContactSalesDialog } from 'core/integrations';
import { useNavigateToReportingPlans } from 'features/organization/management/platform-usage';
import { useReportingTrialFleetStats } from '@cast/features-cluster-shared';
import { notReachable } from 'utils/types/not-reachable';
import { PermissionsSummary } from './PermissionsSummary';
import {
  BackButton,
  ClusterDialogTitle,
  DialogHeading,
  FeaturesScriptSplitDialogContent,
  IRanTheScriptButton,
  ScriptSection,
  SimpleFeaturesSection,
  StepContainer,
} from '../../../../_components/new-onboarding';
import { HelmScriptWarning } from '../../../../_components/new-onboarding/HelmScriptWarning';
import { InstallationMethodToggle } from '../../../../_components/new-onboarding/InstallationMethodToggle';
import { TerraformTabContent } from '../../../../_components/new-onboarding/TerraformTabContent';
import { TrialScriptOverlay } from '../../../../components/TrialScriptOverlay';
import { InstallationMethod } from '../../../../types';
import { isPremiumFeatureSelected } from '../../../../utils';
import { OnboardClusterContext } from '../../../OnboardClusterProvider';

type Props = {
  onClose(): void;
  onRunTheScript(): void;
  onBack(): void;
};

export const SelectFeaturesStep = (props: Props) => {
  const {
    provider,
    featureSelection,
    updateFeature,
    autoscalerType,
    setAutoscalerType,
  } = useContext(OnboardClusterContext);
  const shouldShowAutoscalerSelection = provider === K8sProvider.EKS;
  const { isPreTrial } = useTrial();
  const navigateToReportingPlans = useNavigateToReportingPlans();
  const fleet = useReportingTrialFleetStats();
  const { openReportingContactSales } = useReportingContactSalesDialog();
  const hasPremiumFeatureSelected = isPremiumFeatureSelected(featureSelection);

  const [installationMethod, setInstallationMethod] =
    useState<InstallationMethod>('script');

  const hasSelectedFeature = useMemo(
    () => Object.values(featureSelection).some(Boolean),
    [featureSelection]
  );

  const cloud = k8sProviderToCloud[provider];

  const shouldDisplayPermissionsSummary =
    !!cloud && isSupportedCloudProvider(cloud) && hasSelectedFeature;
  const shouldFetchScript =
    installationMethod === 'script' ||
    (installationMethod === 'helm' && !featureSelection.autoscaler);

  const { isLoading, isFetching, error, refetch, script } =
    useConnectAndEnableCastAiScriptQuery({
      clusterProvider: provider,
      installGPUPlugin: featureSelection.nvidiaGpuPlugin,
      installSecurityAgent: featureSelection.security,
      installSecurityCompliance: false,
      installSecurityImageScanning: false,
      installAutoscalerAgent: featureSelection.autoscaler,
      installNetflowExporter: featureSelection.networkCostMetrics,
      installGpuMetricsExporter: featureSelection.gpuMetricsExporter,
      installReliabilityMetrics: false,
      installWorkloadAutoscaler: featureSelection.workloadAutoscaler,
      installOmni: featureSelection.omni,
      installAiOptimizerProxy: false,
      installPodMutator: hasPremiumFeatureSelected,
      onboardingPath:
        installationMethod === 'helm'
          ? ClusterOnboardingPathEnum.HELM
          : undefined,
      enabled: shouldFetchScript,
    });

  const isCastAutoscalerSelected = autoscalerType === 'cast-ai';
  // Currently Helm onboarding doesn't support autoscaler
  const showHelmWarning =
    installationMethod === 'helm' && !!featureSelection.autoscaler;

  const renderRightTitle = () => {
    if (!isCastAutoscalerSelected) {
      return (
        <Typography variant="h6" mb={2}>
          Run script below
        </Typography>
      );
    }

    return (
      <InstallationMethodToggle
        value={installationMethod}
        onChange={setInstallationMethod}
      />
    );
  };

  const trialConnectionAlert = (
    <ReportingTrialConnectionAlert
      sx={{ mt: 16, width: '100%' }}
      fleet={fleet}
      onViewPlans={navigateToReportingPlans}
      onContactSales={() => openReportingContactSales()}
    />
  );

  const renderRightContent = () => {
    let content: ReactNode;

    if (!isCastAutoscalerSelected) {
      content = (
        <KentInstallScript
          selectedFeatures={{
            POD_MUTATIONS: true,
            REBALANCING: true,
            WORKLOAD_RIGHT_SIZING: true,
            SPOT_INTERRUPTION_PREDICTION: true,
            CLM: true,
          }}
        />
      );
    } else {
      switch (installationMethod) {
        case 'script':
        case 'helm':
          content = (
            <ScriptSection
              scriptFooter={
                shouldDisplayPermissionsSummary ? (
                  <PermissionsSummary
                    cloudProvider={cloud}
                    selectedFeatures={featureSelection}
                  />
                ) : undefined
              }
              scriptSx={
                shouldDisplayPermissionsSummary
                  ? {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : undefined
              }
              script={script}
              isLoading={isLoading || isFetching}
              refetch={refetch}
              error={error}
              customScriptContent={
                showHelmWarning ? <HelmScriptWarning /> : undefined
              }
              overlay={
                isPreTrial && hasPremiumFeatureSelected ? (
                  <TrialScriptOverlay script={script} />
                ) : undefined
              }
            />
          );
          break;
        case 'terraform':
          content = <TerraformTabContent />;
          break;
        default:
          return notReachable(installationMethod);
      }
    }

    return (
      <Stack>
        {content}
        {trialConnectionAlert}
      </Stack>
    );
  };

  return (
    <StepContainer data-testid="select-features-step">
      <DialogHeading
        onClose={props.onClose}
        sx={{ borderBottom: tokens.theme.border.structural.neutral }}
        hideTerraformLink
      >
        <Stack direction="row" alignItems="center" gap={8} flexWrap="nowrap">
          <K8sProviderIcon provider={provider} size={24} />
          <ClusterDialogTitle>Connect your K8s cluster</ClusterDialogTitle>
        </Stack>
      </DialogHeading>
      <FeaturesScriptSplitDialogContent
        leftTitle="Choose features"
        left={
          <SimpleFeaturesSection
            featureSelection={featureSelection}
            updateFeature={(feature, selected) => {
              updateFeature(feature, selected);
              if (feature === 'autoscaler') {
                // reset autoscaler type so that
                // 1. non-kent script is used for onboarding without autoscaler
                // 2. cast-ai is default autoscaler type when autoscaler is enabled
                setAutoscalerType('cast-ai');
              }
            }}
            isEKSProvider={provider === K8sProvider.EKS}
            {...(shouldShowAutoscalerSelection
              ? {
                  shouldShowAutoscalerSelection: true,
                  autoscalerType,
                  onSelectAutoscalerType: (autoscalerType) => {
                    setAutoscalerType(autoscalerType);
                  },
                }
              : {
                  shouldShowAutoscalerSelection: false,
                })}
          />
        }
        rightTitle={renderRightTitle()}
        right={renderRightContent()}
      />
      <DialogFooter
        start={<BackButton onClick={props.onBack} />}
        end={
          installationMethod === 'terraform' && isCastAutoscalerSelected ? (
            <Button onClick={props.onClose} testId="terraform-done-button">
              Done
            </Button>
          ) : (
            <IRanTheScriptButton
              onClick={props.onRunTheScript}
              disabled={showHelmWarning}
            />
          )
        }
      />
    </StepContainer>
  );
};
